<template>
  <div class="fechamentos-detalhes" data-testid="fechamentos-detalhes">
    <!-- Dashboard de Indicadores -->
    <div v-if="fechamento">
      <FechamentoDashboard
        :fechamento="(fechamento as any)"
        :resultado-financeiro="resultadoFinanceiro"
        :is-loading="isLoadingResultado"
        @refresh="handleDashboardRefresh"
      />
    </div>

    <!-- Gráfico de Evolução Mensal -->
    <FechamentoEvolucaoChart
      v-if="fechamentosHistoricos && fechamentosHistoricos.length > 1"
      :fechamentos="fechamentosHistoricos"
      class="mb-4"
    />

    <!-- Loading -->
    <div v-if="loadingPlano" class="text-center py-4" data-testid="loading-plano">
      <spinner style="width: 30px; height: 30px;" />
    </div>

    <!-- Plano de Contas Table -->
    <data-table
      v-else
      :config="datatableConfig"
      :data="contas"
      data-testid="datatable-plano-contas"
      style="margin-top: 1rem;"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { permissionsService } from '../../services/permissionsService';
import { usePlanoContas } from '../../composables/usePlanoContas';
import { useFechamentos } from '../../composables/useFechamentos';
import { log } from '../../utils/DebugLogger';

// Import new dashboard and chart components
import FechamentoDashboard from './FechamentoDashboard.vue';
import FechamentoEvolucaoChart from './FechamentoEvolucaoChart.vue';
import KpiProgressBar from '../../ui/KpiProgressBar.vue';

import type { Fechamentos_ShowResponseDados } from '../../contracts/Fechamentos_ShowResponse';
import type { Fechamentos_ResultadoFinanceiroResponseDados } from '../../contracts/Fechamentos_ResultadoFinanceiroResponse';

/**
 * Single-fechamento shape consumed by this details component. Backed by the
 * Fechamentos_ShowResponseDados contract — same shape FechamentosModal uses,
 * so the `edit` event can carry it back to the parent without a cast.
 */
type FechamentoData = Fechamentos_ShowResponseDados;

interface FechamentoHistorico {
  id?: number | string;
  data_inicial?: string;
  faturamento_vendas_realizadas?: number | null;
  numero_clientes?: number | null;
  lucro_mensal?: number | null;
  total_despesas_pagas?: number | null;
  [key: string]: unknown;
}

interface TrendValue {
  anterior: number;
  atual: number;
  percentual: number;
}

interface TrendData {
  faturamento: TrendValue | null;
  clientes: TrendValue | null;
  lucro: TrendValue | null;
  despesas: TrendValue | null;
  ticket_medio: TrendValue | null;
}

interface ContaItem {
  nome_conta_snapshot?: string;
  valor_previsto_snapshot?: number | string;
  valor_efetivo?: number | string;
  variacao_valor?: number | string;
  variacao_percentual?: number | string;
  [key: string]: unknown;
}

interface ContasTotals {
  total_previsto: number;
  total_efetivo: number;
  variacao_percentual: number;
}

/**
 * Resultado financeiro shape used by the dashboard.
 *
 * Backed by Fechamentos_ResultadoFinanceiroResponseDados (metas, plano_contas,
 * acumulado, etc.). `tendencia` is a UI-only field synthesised client-side
 * from historical fechamentos — not part of the backend payload.
 */
type ResultadoFinanceiro = Fechamentos_ResultadoFinanceiroResponseDados & {
  tendencia?: TrendData | null;
};

interface CellRenderParams {
  value: unknown;
}

const { t, n } = useI18n();
const { fetchSnapshot, recalculateSnapshot, getVarianceIcon, getVarianceColor } = usePlanoContas();
const { fetchFechamentos, getResultadoFinanceiro } = useFechamentos();

// Props
const props = withDefaults(
  defineProps<{
    fechamento?: FechamentoData | null;
  }>(),
  {
    fechamento: null,
  }
);

// Emits
const emit = defineEmits<{
  (e: 'edit', fechamento: FechamentoData): void;
  (e: 'close'): void;
  (e: 'refresh'): void;
}>();

// State
const contas = ref<ContaItem[]>([]);
const totals = ref<ContasTotals>({ total_previsto: 0, total_efetivo: 0, variacao_percentual: 0 });
const loadingPlano = ref<boolean>(false);
const isRecalculating = ref<boolean>(false);
const fechamentosHistoricos = ref<FechamentoHistorico[]>([]);
const tendenciaPeriodo = ref<TrendData | null>(null);
const loadingHistoricos = ref<boolean>(false);
const resultadoFinanceiro = ref<ResultadoFinanceiro | null>(null);
const isLoadingResultado = ref<boolean>(false);

// Eficiência Operacional (sourced from local resultadoFinanceiro ref)
const eficienciaPercentual = computed<number>(() => {
  const despesas = resultadoFinanceiro.value?.acumulado?.total_despesas_pagas || 0;
  const previsto = resultadoFinanceiro.value?.plano_contas?.total_previsto || 0;
  if (previsto === 0) return 0;
  return Math.round((despesas / previsto) * 100);
});

const eficienciaBgClass = computed<string>(() => {
  const ef = eficienciaPercentual.value;
  if (ef >= 100) return 'bg-success';
  if (ef >= 80) return 'bg-warning';
  return 'bg-danger';
});

const eficienciaTextClass = computed<string>(() => {
  const ef = eficienciaPercentual.value;
  if (ef >= 100) return 'text-success';
  if (ef >= 80) return 'text-warning';
  return 'text-danger';
});

const eficienciaProgressClass = computed<string>(() => {
  const ef = eficienciaPercentual.value;
  if (ef >= 100) return 'bg-success';
  if (ef >= 80) return 'bg-warning';
  return 'bg-danger';
});

// Permissions
const canClose = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Financeiro']);
});

const canRecalculate = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Financeiro']);
});

// Status label
const statusLabel = (status?: string): string => {
  const labels: Record<string, string> = { aberto: t('Aberto'), fechado: t('Fechado') };
  return labels[status || ''] || status || '-';
};

// Period status card (based on variacao_percentual)
const periodoStatusClass = computed<string>(() => {
  const v = Number(props.fechamento?.resumo?.variacao_percentual) || 0;
  if (v <= 0) return 'status-section--success';
  if (v <= 20) return 'status-section--warning';
  return 'status-section--danger';
});

const periodoStatusIcon = computed<string>(() => {
  const cls = periodoStatusClass.value;
  if (cls === 'status-section--success') return 'pi pi-check-circle';
  if (cls === 'status-section--warning') return 'pi pi-exclamation-triangle';
  return 'pi pi-times-circle';
});

const periodoStatusTitle = computed<string>(() => {
  const cls = periodoStatusClass.value;
  if (cls === 'status-section--success') return t('Período Saudável');
  if (cls === 'status-section--warning') return t('Em Atenção');
  return t('Período Crítico');
});

const periodoStatusSub = computed<string>(() => {
  const v = Number(props.fechamento?.resumo?.variacao_percentual) || 0;
  const signal = v >= 0 ? '+' : '';
  return `${signal}${v}% ${t('vs previsto')}`;
});


// DataTable config
const tableColumns = computed(() => [
  { data: 'nome_conta_snapshot', title: t('Conta') },
  { data: 'valor_previsto_snapshot', title: t('Previsto'), renderType: 'currency' },
  { data: 'valor_efetivo', title: t('Realizado'), renderType: 'currency' },
  {
    data: 'variacao_valor',
    title: t('Variação R$'),
    cellRenderer: ({ value }: CellRenderParams): string => {
      const v = parseFloat(String(value)) || 0;
      const color = v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#6b7280';
      return `<span style="color:${color};font-weight:600;">${n(v, 'currency')}</span>`;
    }
  },
  {
    data: 'variacao_percentual',
    title: t('Variação %'),
    cellRenderer: ({ value }: CellRenderParams): string => {
      const v = parseFloat(String(value)) || 0;
      const color = v >= 100 ? '#10b981' : v >= 80 ? '#f59e0b' : '#ef4444';
      const signal = v > 0 ? '+' : '';
      return `<span style="color:${color};font-weight:600;">${signal}${v.toFixed(1)}%</span>`;
    }
  }
]);

const datatableConfig = computed(() => ({
  tableId: 'fechamentosContasDataTable',
  columns: tableColumns.value,
  pagination: false
}));

// Methods
const loadResultadoFinanceiro = async (): Promise<void> => {
  if (!props.fechamento?.id) return;

  try {
    isLoadingResultado.value = true;
    const resultado = await getResultadoFinanceiro(props.fechamento.id) as ResultadoFinanceiro;
    resultadoFinanceiro.value = {
      ...resultado,
      tendencia: tendenciaPeriodo.value || resultado.tendencia || null
    };
    log('FechamentosDetalhes', 'loadResultadoFinanceiro', 'Resultado loaded', resultadoFinanceiro.value);
  } catch (err) {
    console.error('Erro ao carregar resultado financeiro:', err);
  } finally {
    isLoadingResultado.value = false;
  }
};

const loadPlanoContas = async (): Promise<void> => {
  if (!props.fechamento?.id) return;

  loadingPlano.value = true;
  log('FechamentosDetalhes', 'loadPlanoContas', 'Loading snapshot', { fechamentoId: props.fechamento.id });

  try {
    const result = await fetchSnapshot(Number(props.fechamento.id)) as unknown as { contas?: ContaItem[]; totais?: ContasTotals };

    contas.value = result.contas || [];
    totals.value = result.totais || { total_previsto: 0, total_efetivo: 0, variacao_percentual: 0 };
    log('FechamentosDetalhes', 'loadPlanoContas', 'Snapshot loaded', { count: contas.value.length, totals: totals.value });
  } catch (error) {
    log('FechamentosDetalhes', 'loadPlanoContas', 'Error', error);
  } finally {
    loadingPlano.value = false;
  }
};

const recalcular = async (): Promise<void> => {
  if (!props.fechamento?.id) return;
  isRecalculating.value = true;
  log('FechamentosDetalhes', 'recalcular', 'Recalculating snapshot', { fechamentoId: props.fechamento.id });

  try {
    await recalculateSnapshot(Number(props.fechamento.id));
    await loadPlanoContas();
  } catch (error) {
    log('FechamentosDetalhes', 'recalcular', 'Error', error);
  } finally {
    isRecalculating.value = false;
  }
};

// Handle dashboard refresh event
const handleDashboardRefresh = (): void => {
  log('FechamentosDetalhes', 'handleDashboardRefresh', 'Dashboard refresh requested');
  loadResultadoFinanceiro();
  loadPlanoContas();
};

// Load historical fechamentos for evolution chart
const loadFechamentosHistoricos = async (): Promise<void> => {
  if (!props.fechamento?.franquia_id || !props.fechamento?.data_inicial) return;

  loadingHistoricos.value = true;
  log('FechamentosDetalhes', 'loadFechamentosHistoricos', 'Loading historical fechamentos');

  try {
    // Fetch fechamentos from the same franchise for the current year
    const currentYear = new Date(props.fechamento.data_inicial).getFullYear();
    const dados = await fetchFechamentos({
      franquia_id: props.fechamento.franquia_id,
      data_inicial: `${currentYear}-01-01`,
      data_final: `${currentYear}-12-31`
    }, 1, 12) as { data?: FechamentoHistorico[] } | FechamentoHistorico[];

    const raw = (dados && !Array.isArray(dados) ? dados.data : dados);
    fechamentosHistoricos.value = Array.isArray(raw) ? raw : [];

    // Calculate trend (current month vs previous month)
    await calcularTendencia();
  } catch (error) {
    console.error('Error loading historical fechamentos:', error);
  } finally {
    loadingHistoricos.value = false;
  }
};

// Calculate trend comparing with previous month
const calcularTendencia = async (): Promise<void> => {
  if (fechamentosHistoricos.value.length < 2) {
    tendenciaPeriodo.value = null;
    return;
  }

  // Sort by date
  const sorted = [...fechamentosHistoricos.value].sort((a, b) =>
    new Date(a.data_inicial || 0).getTime() - new Date(b.data_inicial || 0).getTime()
  );

  // Find current and previous month
  const currentIndex = sorted.findIndex(f => f.id === props.fechamento?.id);
  if (currentIndex <= 0) {
    tendenciaPeriodo.value = null;
    return;
  }

  const atual = sorted[currentIndex];
  const anterior = sorted[currentIndex - 1];

  const anteriorClientes = anterior.numero_clientes || 0;
  const atualClientes = atual.numero_clientes || 0;

  // Calculate trend percentages
  tendenciaPeriodo.value = {
    faturamento: calcularPercentualTendencia(
      anterior.faturamento_vendas_realizadas || 0,
      atual.faturamento_vendas_realizadas || 0
    ),
    clientes: calcularPercentualTendencia(
      anteriorClientes,
      atualClientes
    ),
    lucro: calcularPercentualTendencia(
      anterior.lucro_mensal || 0,
      atual.lucro_mensal || 0
    ),
    despesas: calcularPercentualTendencia(
      anterior.total_despesas_pagas || 0,
      atual.total_despesas_pagas || 0
    ),
    ticket_medio: calcularPercentualTendencia(
      anteriorClientes > 0 ? (anterior.faturamento_vendas_realizadas || 0) / anteriorClientes : 0,
      atualClientes > 0 ? (atual.faturamento_vendas_realizadas || 0) / atualClientes : 0
    )
  };

  // Merge tendencia into resultadoFinanceiro if already loaded
  if (resultadoFinanceiro.value && tendenciaPeriodo.value) {
    resultadoFinanceiro.value = {
      ...resultadoFinanceiro.value,
      tendencia: tendenciaPeriodo.value
    };
  }
};

const calcularPercentualTendencia = (anterior: number, atual: number): TrendValue | null => {
  if (anterior === 0) return null;
  const percentual = ((atual - anterior) / anterior) * 100;
  return {
    anterior,
    atual,
    percentual: Math.round(percentual * 10) / 10 // Round to 1 decimal
  };
};

// Helpers
const formatDate = (date?: string | null): string => {
  if (!date) return '-';
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('pt-BR');
};

const getVarianceClass = (variacao: number | string): string => {
  const color = getVarianceColor(variacao);
  switch (color) {
    case 'success': return 'text-success';
    case 'warning': return 'text-warning';
    case 'danger': return 'text-danger';
    default: return 'text-muted';
  }
};

const getVariationClass = (percentual?: number | null): string => {
  if (!percentual) return 'text-muted';
  if (percentual >= 100) return 'text-success';
  if (percentual >= 80) return 'text-warning';
  return 'text-danger';
};

// Watch
watch(() => props.fechamento?.id, () => {
  loadPlanoContas();
  loadFechamentosHistoricos();
  loadResultadoFinanceiro();
});

// Keep tendencia in sync with resultadoFinanceiro when tendencia updates after result is already loaded
watch(tendenciaPeriodo, (novaTendencia) => {
  if (resultadoFinanceiro.value && novaTendencia) {
    resultadoFinanceiro.value = {
      ...resultadoFinanceiro.value,
      tendencia: novaTendencia
    };
  }
}, { deep: true });

// Lifecycle
onMounted(() => {
  loadPlanoContas();
  loadFechamentosHistoricos();
  loadResultadoFinanceiro();
});
</script>

<style scoped>
/* Header 3-column grid - preenche largura disponível */
.detail-header-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  align-items: stretch;
  width: 100%;
}

@media (max-width: 1100px) {
  .detail-header-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .detail-header-grid {
    grid-template-columns: 1fr;
  }
}

.eficiencia-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

/* Info rows inside boxes */
.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  color: #6b7280;
}

/* ============================================
   NOVOS ESTILOS - META DE FATURAMENTO
   ============================================ */

.meta-faturamento-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.meta-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  color: #111827;
}

.meta-sub {
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

/* ============================================
   ESTILOS - STATUS + EFICIÊNCIA COMBINADO
   ============================================ */

.status-eficiencia-combined {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0;
}

/* Seção Superior: Status do Período */
.status-section {
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-section--success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-section--warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.status-section--danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.status-section-icon {
  flex-shrink: 0;
}

.status-section-icon i {
  font-size: 1.5rem;
  opacity: 0.9;
}

.status-section-body {
  flex: 1;
}

.status-section-title {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.2;
}

.status-section-sub {
  font-size: 0.75rem;
  opacity: 0.85;
}

/* Divider entre seções */
.combined-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
}

/* Seção Inferior: Eficiência Operacional */
.eficiencia-section {
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.eficiencia-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.eficiencia-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #6b7280;
}

.eficiencia-section .eficiencia-value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}

/* ============================================
   ESTILOS PARA GARANTIR ALTURA IGUAL
   ============================================ */

/* Garantir que os 2 cards tenham mesma altura */
.detail-header-grid > :deep(.box),
.detail-header-grid > box {
  height: 100%;
  min-height: 160px;
  display: flex;
  flex-direction: column;
}

.detail-header-grid > :deep(.box-content),
.detail-header-grid > box :deep(.box-content) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

</style>
